import React, { useEffect, useState, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { applyActionCode, confirmPasswordReset, verifyPasswordResetCode } from 'firebase/auth';
import { auth } from '../../lib/firebase';
import { useAuth } from '../../contexts/auth-context';

export default function Action() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { logout } = useAuth();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [success, setSuccess] = useState(false);
  const hasAttemptedVerification = useRef(false);
  const mode = searchParams.get('mode');
  const oobCode = searchParams.get('oobCode');

  useEffect(() => {
    const handleAction = async () => {
      if (!oobCode || !['verifyEmail', 'resetPassword', 'verifyAndChangeEmail'].includes(mode)) {
        navigate('/', { replace: true });
        return;
      }

      try {
        if ((mode === 'verifyEmail' || mode === 'verifyAndChangeEmail') && !hasAttemptedVerification.current) {
          hasAttemptedVerification.current = true;
          await applyActionCode(auth, oobCode);
          
          // For email change, logout the user and redirect to login
          if (mode === 'verifyAndChangeEmail') {
            await logout();
          }
          
          setSuccess(true);
        } else if (mode === 'resetPassword') {
          await verifyPasswordResetCode(auth, oobCode);
        }
      } catch (err) {
        setError(err.message);
      } finally {
        setLoading(false);
      }
    };

    handleAction();
  }, [mode, oobCode, navigate, logout]);

  const handlePasswordReset = async (e) => {
    e.preventDefault();
    if (!newPassword) {
      setError('Please enter a new password');
      return;
    }

    try {
      setLoading(true);
      await confirmPasswordReset(auth, oobCode, newPassword);
      setSuccess(true);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="text-white">Processing...</div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="bg-gray-800/30 rounded-xl p-8 max-w-md w-full mx-4">
          <div className="text-red-400 mb-4">Error: {error}</div>
          <button
            onClick={() => navigate('/')}
            className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white hover:opacity-90 transition-opacity"
          >
            Return Home
          </button>
        </div>
      </div>
    );
  }

  if (success) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="bg-gray-800/30 rounded-xl p-8 max-w-md w-full mx-4">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text mb-4">
            {mode === 'verifyAndChangeEmail' ? 'Email Changed Successfully!' : mode === 'verifyEmail' ? 'Email Verified!' : 'Password Reset Complete!'}
          </h2>
          <p className="text-gray-300 mb-6">
            {mode === 'verifyAndChangeEmail'
              ? 'Your new email has been verified and updated. Please log in again with your new email.'
              : mode === 'verifyEmail'
              ? 'Your email has been verified. You can now use all features of the app.'
              : 'Your password has been reset successfully. You can now log in with your new password.'}
          </p>
          <button
            onClick={() => navigate('/login')}
            className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white hover:opacity-90 transition-opacity"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  if (mode === 'resetPassword') {
    return (
      <div className="min-h-screen bg-gradient-to-br from-gray-900 via-black to-gray-900 flex items-center justify-center">
        <div className="bg-gray-800/30 rounded-xl p-8 max-w-md w-full mx-4">
          <h2 className="text-2xl font-bold bg-gradient-to-r from-purple-400 to-pink-600 text-transparent bg-clip-text mb-4">
            Reset Your Password
          </h2>
          <form onSubmit={handlePasswordReset}>
            <input
              type="password"
              value={newPassword}
              onChange={(e) => setNewPassword(e.target.value)}
              placeholder="Enter new password"
              className="w-full px-4 py-2 rounded-lg bg-gray-700 text-white border border-gray-600 focus:border-purple-500 focus:outline-none mb-4"
              minLength="6"
            />
            <button
              type="submit"
              className="w-full px-4 py-2 bg-gradient-to-r from-purple-500 to-pink-600 rounded-lg text-white hover:opacity-90 transition-opacity"
            >
              Reset Password
            </button>
          </form>
        </div>
      </div>
    );
  }
}